import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle } from 'lucide-react';
import { useSurveyTabAutoClose } from '../../hooks/useSurveyTabClose';

export default function SurveyScreenoutPage() {
  const [params] = useSearchParams();
  // Real consolation from the provider redirect (?points=N). May be 0 when the
  // session was too quick to earn consolation (T7 zero-consolation copy).
  const consolation = Number(params.get('points') ?? '0');
  const [countdown, setCountdown] = useState(8);
  const { showCloseButton } = useSurveyTabAutoClose({ type: 'screenout', consolation });

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = '/surveys';
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center space-y-6"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <AlertCircle className="w-20 h-20 text-[#F59E0B] mx-auto" strokeWidth={1.5} />
        </motion.div>

        <h1 className="text-2xl font-bold font-display text-[#111827]">
          Not a Match This Time
        </h1>

        <p className="text-sm text-[#6B7280]">
          {consolation > 0
            ? "You weren't the right fit, but earned consolation points for trying"
            : "You weren't the right fit — happens to everyone, it's not about you"}
        </p>

        {consolation > 0 ? (
          <div className="inline-flex items-center gap-2 bg-[#F59E0B]/10 text-[#F59E0B] font-bold text-lg px-5 py-2.5 rounded-full">
            +{consolation} pts (available immediately)
          </div>
        ) : (
          <p className="text-sm text-[#6B7280]">
            This one was too quick to earn consolation points — give the next a couple of minutes.
          </p>
        )}

        <p className="text-sm text-[#6B7280]">
          New surveys are added daily
        </p>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <Link
            to="/surveys"
            className="flex-1 py-3 rounded-full bg-[#5B4FE8] text-white text-sm font-medium hover:bg-[#4338CA] transition-colors text-center"
          >
            Browse Surveys
          </Link>
          <Link
            to="/dashboard"
            className="flex-1 py-3 rounded-full border-2 border-[#E5E7EB] text-[#111827] text-sm font-medium hover:bg-gray-50 transition-colors text-center"
          >
            Go to Dashboard
          </Link>
        </div>

        {showCloseButton ? (
          <button
            onClick={() => window.close()}
            className="text-sm text-[#5B4FE8] font-medium hover:underline"
          >
            Close this tab
          </button>
        ) : (
          <p className="text-xs text-[#9CA3AF]">
            Redirecting to surveys in {countdown}s...
          </p>
        )}
      </motion.div>
    </div>
  );
}
